import React, { useEffect, useRef, useMemo } from 'react';
import { View, Animated, Platform, ScrollView } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { getWcColors } from './matchPickStats.helper';
import { getPickStatsSkeletonStyles } from './PickStatsScreenSkeleton.styles';

function Bone({ w, h, radius = 8, boneStyle }: { w: number | string; h: number; radius?: number; boneStyle: object }) {
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.35, duration: 750, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(opacity, { toValue: 1, duration: 750, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    ).start();
  }, [opacity]);

  return (
    <Animated.View
      style={[boneStyle, { width: w as never, height: h, borderRadius: radius, opacity }]}
    />
  );
}

export const PickStatsScreenSkeleton: React.FC = () => {
  const theme = useTheme();
  const C = useMemo(() => getWcColors(theme), [theme]);
  const sk = useMemo(() => getPickStatsSkeletonStyles(C), [C]);

  return (
    <View style={sk.root}>
      <View style={sk.backRow}>
        <Bone w={16} h={16} radius={4} boneStyle={sk.bone} />
        <Bone w={36} h={12} radius={4} boneStyle={sk.bone} />
      </View>

      <View style={sk.header}>
        <View style={sk.teamsRow}>
          <Bone w="30%" h={14} radius={4} boneStyle={sk.bone} />
          <Bone w={52} h={26} radius={6} boneStyle={sk.bone} />
          <Bone w="30%" h={14} radius={4} boneStyle={sk.bone} />
        </View>
        <Bone w={80} h={10} radius={4} boneStyle={sk.bone} />
      </View>

      <View style={sk.scopeWrap}>
        <View style={sk.scopeToggle}>
          <Bone w="47%" h={36} radius={10} boneStyle={sk.bone} />
          <Bone w="47%" h={36} radius={10} boneStyle={sk.bone} />
        </View>
      </View>

      <ScrollView scrollEnabled={false} showsVerticalScrollIndicator={false} contentContainerStyle={sk.scroll}>
        <View style={sk.sectionWrap}>
          <Bone w={80} h={10} radius={4} boneStyle={sk.bone} />
          <View style={[sk.panel, { marginTop: 8 }]}>
            <View style={sk.mRow}>
              <Bone w={50} h={10} radius={4} boneStyle={sk.bone} />
              <View style={sk.chips}>
                <Bone w="30%" h={44} radius={8} boneStyle={sk.bone} />
                <Bone w="30%" h={44} radius={8} boneStyle={sk.bone} />
                <Bone w="30%" h={44} radius={8} boneStyle={sk.bone} />
              </View>
            </View>
            <View style={[sk.mRow, sk.mRowLast]}>
              <Bone w={50} h={10} radius={4} boneStyle={sk.bone} />
              <View style={sk.chips}>
                <Bone w="47%" h={44} radius={8} boneStyle={sk.bone} />
                <Bone w="47%" h={44} radius={8} boneStyle={sk.bone} />
              </View>
            </View>
          </View>
        </View>

        <View style={sk.sectionWrap}>
          <Bone w={120} h={10} radius={4} boneStyle={sk.bone} />
          <View style={[sk.panel, sk.piePanel]}>
            <Bone w={140} h={140} radius={70} boneStyle={sk.bone} />
            <View style={sk.legend}>
              <View style={sk.legendRow}><Bone w={10} h={10} radius={5} boneStyle={sk.bone} /><Bone w={90} h={10} radius={4} boneStyle={sk.bone} /></View>
              <View style={sk.legendRow}><Bone w={10} h={10} radius={5} boneStyle={sk.bone} /><Bone w={90} h={10} radius={4} boneStyle={sk.bone} /></View>
              <View style={sk.legendRow}><Bone w={10} h={10} radius={5} boneStyle={sk.bone} /><Bone w={90} h={10} radius={4} boneStyle={sk.bone} /></View>
            </View>
          </View>
        </View>

        <View style={sk.sectionWrap}>
          <Bone w={100} h={10} radius={4} boneStyle={sk.bone} />
          <View style={[sk.panel, sk.heatPanel]}>
            {[0, 1, 2, 3, 4].map((r) => (
              <View key={r} style={sk.heatRow}>
                {[0, 1, 2, 3, 4].map((c) => (
                  <Bone key={c} w={38} h={38} radius={8} boneStyle={sk.bone} />
                ))}
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
};
